import { useEffect, useRef, useState } from "react";

import { Column, Row, Text } from "@/components";
import {
  WinterFinishGate,
  WinterFinishSigil,
  WinterRaceLane,
} from "@/components/leaderboard";
import { RacetrackPetRacer } from "@/components/leaderboard/RacetrackPetRacer";
import type { RacetrackGoalScope } from "@/lib/leaderboardGoal";
import { racetrackLanes } from "@/lib/racetrackLanes";
import { teamGoals } from "@/lib/teamGoals";
import { cn } from "@/lib/utils";

import type { LeaderboardUser, PetAppearance } from "@/types";

/* Constants */

// Repeating white dashes drawn as an inline SVG, tiled across each lane.
const LANE_DASH_PATTERN = `url("data:image/svg+xml,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="3" viewBox="0 0 18 3"><rect width="6" height="3" rx="1.5" fill="white" fill-opacity="0.7"/></svg>',
)}")`;

const FIT_LANE_GAP_PX = 12;
const FIT_LANE_GAP_RATIO = 0.15;
const FIT_LABEL_FONT_SIZE_PX = 14;
const FIT_PET_SIZE_PX = 72;

// Pet position along the lane, scaled to the finish line: 0 revenue sits at the
// start, hitting the finish reaches the flag. Clamped so beating it doesn't
// overshoot.
const LANE_PROGRESS_MIN_PERCENT = 7;
const LANE_PROGRESS_MAX_PERCENT = 100;

// Once the leader passes the goal, the finish line extends to the leader's
// revenue rounded up to the next step, so the race keeps going.
const REVENUE_FINISH_STEP = 5000;

const progressForRevenue = (revenue: number, target: number): number => {
  if (target <= 0) return LANE_PROGRESS_MIN_PERCENT;
  const ratio = Math.min(revenue / target, 1);
  return (
    LANE_PROGRESS_MIN_PERCENT +
    ratio * (LANE_PROGRESS_MAX_PERCENT - LANE_PROGRESS_MIN_PERCENT)
  );
};

/**
 * Revenue track: one labeled lane per racer, each with the staffer's pet
 * positioned by revenue. Last month's winner wears their pet's crown topper.
 */
export const RevenueRacetrack = ({
  users,
  revenueGoalPerStaffer,
  goalScope,
  isWinter = false,
  isFitToHeight = false,
  petByUserId,
  inactiveUserIds,
  className,
}: {
  users: LeaderboardUser[];
  revenueGoalPerStaffer?: number;
  goalScope?: RacetrackGoalScope | null;
  isWinter?: boolean;
  isFitToHeight?: boolean;
  petByUserId?: Map<number, PetAppearance>;
  inactiveUserIds?: Set<number>;
  className?: string;
}) => {
  /* React Hooks */
  const laneColumnRef = useRef<HTMLDivElement>(null);
  const [laneAreaHeightPx, setLaneAreaHeightPx] = useState(0);
  const [isEntryWindowOpen, setIsEntryWindowOpen] = useState(true);

  // The run-in plays once per page load; racers that join later start in place.
  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setIsEntryWindowOpen(false);
    }, racetrackLanes.ENTRY_DURATION_MS);
    return () => {
      window.clearTimeout(timeout);
    };
  }, []);

  useEffect(() => {
    const laneColumn = laneColumnRef.current;
    if (!isFitToHeight || !laneColumn) return;

    const observer = new ResizeObserver(([entry]) => {
      if (entry) setLaneAreaHeightPx(entry.contentRect.height);
    });
    observer.observe(laneColumn);
    return () => observer.disconnect();
  }, [isFitToHeight, users.length]);

  /* Helpers */
  // Lane order = last month's revenue ranking, so lanes stay put as the viewed
  // date range changes. Pet position still reflects the selected period.
  const lanes = [...users].sort(
    (a, b) =>
      b.previousMonthRevenue - a.previousMonthRevenue || b.revenue - a.revenue,
  );
  const maxRevenue = Math.max(...lanes.map((u) => u.revenue), 0);
  // The goal is the finish line until the leader passes it; then the finish
  // extends to the leader's revenue rounded up to the next step, and the goal
  // becomes an intermediate marker so leaders can keep racing past it.
  const passedGoal =
    revenueGoalPerStaffer !== undefined && maxRevenue > revenueGoalPerStaffer;
  // With no goal set, fall back to a leader-relative race.
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const baseTarget = revenueGoalPerStaffer || maxRevenue;
  const trackTarget = passedGoal
    ? Math.ceil(maxRevenue / REVENUE_FINISH_STEP) * REVENUE_FINISH_STEP
    : baseTarget;
  // Where the original goal sits on the extended track (null until it's passed).
  const goalMarkerPercent = passedGoal
    ? progressForRevenue(revenueGoalPerStaffer, trackTarget)
    : null;
  // On-track marker label, e.g. "Weekly Goal $16K" (only set once passed).
  const goalMarkerLabel = passedGoal
    ? `${goalScope === "WEEK" ? "Weekly" : "Monthly"} Goal ${teamGoals.formatRevenueGoal(revenueGoalPerStaffer)}`
    : "";
  let laneGapPx = racetrackLanes.GAP_PX;
  if (isFitToHeight) {
    laneGapPx = FIT_LANE_GAP_PX;
    if (laneAreaHeightPx > 0 && lanes.length > 0) {
      laneGapPx = Math.min(
        FIT_LANE_GAP_PX,
        (laneAreaHeightPx / lanes.length) * FIT_LANE_GAP_RATIO,
      );
    }
  }
  const laneHeightPx =
    isFitToHeight && laneAreaHeightPx > 0 && lanes.length > 0
      ? Math.max(0, laneAreaHeightPx - laneGapPx * (lanes.length - 1)) /
        lanes.length
      : racetrackLanes.PET_HEIGHT_PX;
  const laneHeightStyle = { height: laneHeightPx };

  // Crossed the line: hit the goal, or lead a goal-less race.
  const hasFinished = (user: LeaderboardUser): boolean => {
    if (revenueGoalPerStaffer) return user.revenue >= revenueGoalPerStaffer;
    return maxRevenue > 0 && user.revenue === maxRevenue;
  };

  /* Render */
  // Top-right marker. Normally the weekly/monthly goal; once the goal is passed,
  // the finish line is the rounded-up ceiling, so it shows that instead (the goal
  // itself moves to the on-track dashed marker).
  const renderFinishGoal = () => {
    if (!revenueGoalPerStaffer) return null;

    return (
      <Row
        className={cn("items-center justify-end gap-1.5", isWinter && "pr-24")}
      >
        <Text className="text-xs font-medium uppercase tracking-wider text-slate-300">
          {passedGoal
            ? "Finish"
            : `${goalScope === "WEEK" ? "Weekly" : "Monthly"} Goal`}
        </Text>
        <Text className="text-sm font-semibold text-white">
          {teamGoals.formatRevenueGoal(
            passedGoal ? trackTarget : revenueGoalPerStaffer,
          )}
        </Text>
      </Row>
    );
  };

  const renderLane = (user: LeaderboardUser, laneIndex: number) => (
    <Row
      key={user.userId}
      className="relative shrink-0 items-center gap-2 overflow-visible"
      style={{ ...laneHeightStyle, zIndex: laneIndex + 1 }}
    >
      <Row
        className={cn(
          "relative min-w-0 flex-1 items-center overflow-visible",
          !isFitToHeight && "min-h-[28px]",
          !isWinter && "bg-repeat-x",
        )}
        style={
          isWinter
            ? undefined
            : {
                backgroundImage: LANE_DASH_PATTERN,
                backgroundPosition: "center",
              }
        }
      >
        {isWinter && (
          <WinterRaceLane
            laneHeightPx={laneHeightPx}
            progressPercent={progressForRevenue(user.revenue, trackTarget)}
          />
        )}
        {goalMarkerPercent !== null && (
          <div
            className="pointer-events-none absolute bottom-0 top-0 z-0 border-l-2 border-dashed border-white"
            style={{ left: `${goalMarkerPercent.toString()}%` }}
          >
            {laneIndex === 0 && (
              <Text className="absolute -top-5 left-0 -translate-x-1/2 whitespace-nowrap text-[10px] font-semibold uppercase tracking-wider text-white">
                {goalMarkerLabel}
              </Text>
            )}
          </div>
        )}
        <RacetrackPetRacer
          progressPercent={progressForRevenue(user.revenue, trackTarget)}
          laneZIndex={laneIndex + 1}
          pet={petByUserId?.get(user.userId)}
          sizePx={isFitToHeight ? FIT_PET_SIZE_PX : undefined}
          maxHeightPx={isFitToHeight ? laneHeightPx : undefined}
          isEntryAnimated={isEntryWindowOpen}
        />
      </Row>
      <Row className="w-8 shrink-0 justify-center">
        {renderFinishMarker(user)}
      </Row>
    </Row>
  );

  const renderFinishMarker = (user: LeaderboardUser) => {
    if (isWinter) {
      return (
        <WinterFinishSigil
          isFinished={hasFinished(user)}
          laneHeightPx={laneHeightPx}
        />
      );
    }

    return <Text className="text-[22.5px] leading-none">🏁</Text>;
  };

  const render = () => {
    if (lanes.length === 0) return null;

    return (
      <Column
        className={cn(
          "min-w-0 flex-1 gap-2",
          isFitToHeight && "h-full min-h-0",
          className,
        )}
      >
        {renderFinishGoal()}
        <Row className="min-h-0 min-w-0 flex-1 items-stretch gap-3 overflow-visible py-1">
          <Column
            className="w-max shrink-0 justify-center text-right"
            style={{ gap: laneGapPx }}
          >
            {lanes.map((user) => (
              <Row
                key={user.userId}
                className="min-w-0 shrink-0 items-center justify-end gap-1 whitespace-nowrap"
                style={{
                  ...laneHeightStyle,
                  fontSize: isFitToHeight
                    ? Math.min(FIT_LABEL_FONT_SIZE_PX, laneHeightPx)
                    : undefined,
                }}
              >
                <Text
                  truncate
                  className={cn(
                    "text-xs font-medium leading-none text-slate-200",
                    isFitToHeight && "[font-size:inherit]",
                  )}
                >
                  {user.firstName}
                </Text>
                {inactiveUserIds?.has(user.userId) && (
                  <Text
                    className={cn(
                      "shrink-0 text-xs font-normal leading-none text-slate-400",
                      isFitToHeight && "[font-size:inherit]",
                    )}
                  >
                    (inactive)
                  </Text>
                )}
              </Row>
            ))}
          </Column>
          <Column
            ref={laneColumnRef}
            className="relative min-w-0 flex-1 justify-center overflow-visible"
            style={{ gap: laneGapPx }}
          >
            {isWinter && <WinterFinishGate />}
            {lanes.map(renderLane)}
          </Column>
        </Row>
      </Column>
    );
  };

  return render();
};
